import React, { createContext, useContext, useState, useCallback, ReactNode, useRef } from 'react';
import { UseCaseMode, FileStates, FileUploadState, MultipleFileUploadState } from '../components/CustomProfilesTesting/types';
import { createEmptyFileStates } from '../components/CustomProfilesTesting/utils';

interface ModeOptions {
    withPreProcessing: boolean;
    mergeDatasets: boolean;
    validationMode: 'single' | 'igm' | '';
    preProcessingMode: 'with' | 'without' | '';
}

const createDefaultOptions = (): ModeOptions => ({
    withPreProcessing: true,
    mergeDatasets: false,
    validationMode: '',
    preProcessingMode: '',
});

export interface GeneratedTtlFile {
    fileName: string;
    displayName?: string;
    downloadUrl: string;
    sessionName?: string;
    generatedAt: number;
}

export type GeneratedTtlSection = 'schemaDriven' | 'contextDriven';

export type GeneratedTtlFiles = Record<GeneratedTtlSection, GeneratedTtlFile[]>;

const createEmptyGeneratedTtlFiles = (): GeneratedTtlFiles => ({
    schemaDriven: [],
    contextDriven: [],
});

interface FileStatesContextType {
    fileStatesByMode: Record<UseCaseMode, FileStates>;
    optionsByMode: Record<UseCaseMode, ModeOptions>;
    generatedTtlFiles: GeneratedTtlFiles;
    updateFileState: (mode: UseCaseMode, key: Exclude<keyof FileStates, 'ttl' | 'rdf' | 'xml'>, newState: FileUploadState) => void;
    updateTtlFiles: (mode: UseCaseMode, updater: MultipleFileUploadState | ((prev: MultipleFileUploadState) => MultipleFileUploadState)) => void;
    updateRdfFiles: (mode: UseCaseMode, updater: MultipleFileUploadState | ((prev: MultipleFileUploadState) => MultipleFileUploadState)) => void;
    updateXmlFiles: (mode: UseCaseMode, updater: MultipleFileUploadState | ((prev: MultipleFileUploadState) => MultipleFileUploadState)) => void;
    updateModeOptions: (mode: UseCaseMode, updates: Partial<ModeOptions>) => void;
    resetFileStates: (mode: UseCaseMode) => void;
    resetAllFileStates: () => void;
    setCurrentSessionId: (sessionId: string | undefined) => void;
    addGeneratedTtlFile: (section: GeneratedTtlSection, file: GeneratedTtlFile) => void;
}

const FileStatesContext = createContext<FileStatesContextType | null>(null);

interface FileStatesProviderProps {
    children: ReactNode;
}

const createEmptyStates = () => ({
    Generation_NCD: createEmptyFileStates(),
    Validation: createEmptyFileStates(),
    Export_CD_Template: createEmptyFileStates(),
    Generation_CD: createEmptyFileStates(),
});

const createDefaultOptionsByMode = () => ({
    Generation_NCD: createDefaultOptions(),
    Validation: createDefaultOptions(),
    Export_CD_Template: createDefaultOptions(),
    Generation_CD: createDefaultOptions(),
});

export const FileStatesProvider: React.FC<FileStatesProviderProps> = ({ children }) => {
    const [fileStatesByMode, setFileStatesByMode] = useState<Record<UseCaseMode, FileStates>>(createEmptyStates);
    const [optionsByMode, setOptionsByMode] = useState<Record<UseCaseMode, ModeOptions>>(createDefaultOptionsByMode);
    const [generatedTtlFiles, setGeneratedTtlFiles] = useState<GeneratedTtlFiles>(createEmptyGeneratedTtlFiles);
    const currentSessionIdRef = useRef<string | undefined>(undefined);

    const setCurrentSessionId = useCallback((sessionId: string | undefined) => {
        if (currentSessionIdRef.current !== sessionId) {
            currentSessionIdRef.current = sessionId;
            setFileStatesByMode(createEmptyStates());
            setOptionsByMode(createDefaultOptionsByMode());
            setGeneratedTtlFiles(createEmptyGeneratedTtlFiles());
        }
    }, []);

    const updateFileState = useCallback((mode: UseCaseMode, key: Exclude<keyof FileStates, 'ttl' | 'rdf' | 'xml'>, newState: FileUploadState) => {
        setFileStatesByMode(prev => ({
            ...prev,
            [mode]: { ...prev[mode], [key]: newState },
        }));
    }, []);

    const updateTtlFiles = useCallback((
        mode: UseCaseMode,
        updater: MultipleFileUploadState | ((prev: MultipleFileUploadState) => MultipleFileUploadState)
    ) => {
        setFileStatesByMode(prev => ({
            ...prev,
            [mode]: {
                ...prev[mode],
                ttl: typeof updater === 'function' ? updater(prev[mode].ttl) : updater,
            },
        }));
    }, []);

    const updateRdfFiles = useCallback((
        mode: UseCaseMode,
        updater: MultipleFileUploadState | ((prev: MultipleFileUploadState) => MultipleFileUploadState)
    ) => {
        setFileStatesByMode(prev => ({
            ...prev,
            [mode]: {
                ...prev[mode],
                rdf: typeof updater === 'function' ? updater(prev[mode].rdf) : updater,
            },
        }));
    }, []);

    const updateXmlFiles = useCallback((
        mode: UseCaseMode,
        updater: MultipleFileUploadState | ((prev: MultipleFileUploadState) => MultipleFileUploadState)
    ) => {
        setFileStatesByMode(prev => ({
            ...prev,
            [mode]: {
                ...prev[mode],
                xml: typeof updater === 'function' ? updater(prev[mode].xml) : updater,
            },
        }));
    }, []);

    const updateModeOptions = useCallback((mode: UseCaseMode, updates: Partial<ModeOptions>) => {
        setOptionsByMode(prev => ({
            ...prev,
            [mode]: { ...prev[mode], ...updates },
        }));
    }, []);

    const resetFileStates = useCallback((mode: UseCaseMode) => {
        setFileStatesByMode(prev => ({
            ...prev,
            [mode]: createEmptyFileStates(),
        }));
    }, []);

    const resetAllFileStates = useCallback(() => {
        setFileStatesByMode(createEmptyStates());
        setOptionsByMode(createDefaultOptionsByMode());
        setGeneratedTtlFiles(createEmptyGeneratedTtlFiles());
    }, []);

    const addGeneratedTtlFile = useCallback((section: GeneratedTtlSection, file: GeneratedTtlFile) => {
        if (!file?.fileName || !file?.downloadUrl) return;
        setGeneratedTtlFiles(prev => {
            const existing = prev[section].filter(f => f.fileName !== file.fileName);
            return {
                ...prev,
                [section]: [...existing, file],
            };
        });
    }, []);

    return (
        <FileStatesContext.Provider value={{
            fileStatesByMode,
            optionsByMode,
            generatedTtlFiles,
            updateFileState,
            updateTtlFiles,
            updateRdfFiles,
            updateXmlFiles,
            updateModeOptions,
            resetFileStates,
            resetAllFileStates,
            setCurrentSessionId,
            addGeneratedTtlFile,
        }}>
            {children}
        </FileStatesContext.Provider>
    );
};

export const useFileStatesContext = (): FileStatesContextType => {
    const context = useContext(FileStatesContext);
    if (!context) {
        throw new Error('useFileStatesContext must be used within a FileStatesProvider');
    }
    return context;
};
